import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtErr } from "../api";
import { Badge } from "../Badge";
import { formatDate, formatTime } from "./CaregiverDashboard";
import { ArrowLeft, MapPin, Phone, ClipboardCheck, CheckCircle2 } from "lucide-react";

const Row = ({ label, value }) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</p>
    <p className="mt-1 text-[15px] text-brand-navy font-medium whitespace-pre-wrap">{value || "—"}</p>
  </div>
);

export default function VisitDetail() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [completeNote, setCompleteNote] = useState("");
  const [completing, setCompleting] = useState(false);

  const load = () => api.get(`/caregiver/visits/${id}`).then((r) => setV(r.data)).catch(() => setV(false));

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const markComplete = async () => {
    if (!window.confirm("Mark this visit as completed?")) return;
    setCompleting(true);
    try {
      await api.post(`/caregiver/visits/${id}/complete`, { note: completeNote });
      toast.success("Visit marked as completed. Thank you!");
      setCompleteNote("");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setCompleting(false);
    }
  };

  if (v === null) return <p className="text-slate-500 font-semibold">Loading visit…</p>;
  if (v === false) return <p className="text-red-700 font-semibold">This visit is not available. It may be assigned to another caregiver or has been removed.</p>;

  return (
    <div data-testid="visit-detail">
      <Link to="/caregiver/visits" data-testid="back-to-visits" className="inline-flex items-center gap-2 text-sm font-bold text-brand-teal hover:text-brand-navy">
        <ArrowLeft className="h-4 w-4" /> Back to Upcoming Visits
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
        <h1 className="font-display text-3xl md:text-4xl text-brand-navy">{v.patient_name}</h1>
        <Badge kind="visit" value={v.status} />
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Row label="Scheduled Date" value={formatDate(v.date)} />
          <Row label="Time" value={`${formatTime(v.start_time)} – ${formatTime(v.end_time)}`} />
          <Row label="Type of Care" value={v.care_type} />
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Visit Address</p>
            <p className="mt-1 flex items-start gap-1.5 text-[15px] text-brand-navy font-medium">
              <MapPin className="h-4 w-4 mt-1 shrink-0 text-brand-teal" /> {v.patient_address || "—"}
            </p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Patient Contact</p>
            <p className="mt-1 text-[15px] text-brand-navy font-medium">
              {v.patient_phone ? (
                <a href={`tel:${v.patient_phone.replace(/[^+\d]/g, "")}`} data-testid="visit-patient-phone" className="inline-flex items-center gap-1.5 text-brand-teal hover:text-brand-navy">
                  <Phone className="h-4 w-4" /> {v.patient_phone}
                </a>
              ) : "—"}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-6">
        <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
          <h2 className="font-display text-xl text-brand-navy">Care Instructions</h2>
          <p className="mt-3 text-[15px] leading-relaxed text-slate-700 whitespace-pre-wrap">{v.instructions || "No specific instructions for this visit."}</p>
          {v.plan_instructions && (
            <div className="mt-4 rounded-xl bg-brand-mist/60 p-4">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-brand-teal">From Care Plan</p>
              <p className="mt-2 text-[15px] leading-relaxed text-slate-700 whitespace-pre-wrap">{v.plan_instructions}</p>
            </div>
          )}
        </section>

        {(v.notes || v.plan_mobility) && (
          <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
            <h2 className="font-display text-xl text-brand-navy">Important Care Notes</h2>
            {v.notes && <p className="mt-3 text-[15px] leading-relaxed text-slate-700 whitespace-pre-wrap">{v.notes}</p>}
            {v.plan_mobility && (
              <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-amber-800">Mobility & Safety</p>
                <p className="mt-2 text-[15px] leading-relaxed text-slate-700 whitespace-pre-wrap">{v.plan_mobility}</p>
              </div>
            )}
          </section>
        )}

        {v.status === "Completed" ? (
          <section data-testid="visit-completed-card" className="rounded-2xl border border-green-200 bg-green-50 p-6 md:p-8">
            <h2 className="font-display text-xl text-brand-navy flex items-center gap-2">
              <CheckCircle2 className="h-6 w-6 text-green-600" strokeWidth={1.8} /> Visit Completed
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Completed by {v.completed_by || "caregiver"}{v.completed_at ? ` on ${new Date(v.completed_at).toLocaleString()}` : ""}
            </p>
            {v.completion_note && (
              <div className="mt-4 rounded-xl bg-white border border-green-200 p-4">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-green-800">Visit Note</p>
                <p className="mt-2 text-[15px] leading-relaxed text-slate-700 whitespace-pre-wrap">{v.completion_note}</p>
              </div>
            )}
          </section>
        ) : v.status !== "Cancelled" && (
          <section className="rounded-2xl border border-brand-teal/30 bg-white p-6 md:p-8">
            <h2 className="font-display text-xl text-brand-navy flex items-center gap-2">
              <ClipboardCheck className="h-6 w-6 text-brand-teal" strokeWidth={1.8} /> Complete This Visit
            </h2>
            <p className="mt-2 text-sm text-slate-600">When the visit is finished, add a short note about how it went and mark it as completed. The office will see the update right away.</p>
            <label htmlFor="completion-note" className="mt-4 block text-sm font-bold text-brand-navy">Visit Note (optional)</label>
            <textarea
              id="completion-note"
              data-testid="completion-note-input"
              rows={3}
              value={completeNote}
              onChange={(e) => setCompleteNote(e.target.value)}
              placeholder="e.g. Assisted with morning routine and prepared lunch. Patient in good spirits."
              className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-teal/60 focus:border-brand-teal"
            />
            <button
              onClick={markComplete}
              disabled={completing}
              data-testid="mark-complete-button"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-teal px-6 py-4 text-base font-bold text-white hover:bg-brand-navy transition-colors duration-300 active:scale-[0.98] disabled:opacity-60"
            >
              <CheckCircle2 className="h-5 w-5" strokeWidth={1.8} />
              {completing ? "Saving…" : "Mark Visit as Completed"}
            </button>
          </section>
        )}
      </div>
    </div>
  );
}
